// oxlint-disable react/only-export-components
import {mergeDefined} from '../../utils/index.js'
import {Accordion, Box, BoxProps, Text, Avatar, Flex, TextProps} from '@chakra-ui/react'
import {createContext, ReactNode, useContext} from 'react'

interface Context {
  /**
   * If defined, the detail card will be collapsible. It is used as a value for the accordion item.
   */
  collapsible?: string
}

const DetailCardContext = createContext<Context>({} as Context)

function useDetailCardContext() {
  return useContext(DetailCardContext)
}

interface DetailCardRootProps extends Context, BoxProps {}

function DetailCardRoot({children, collapsible, ...props}: DetailCardRootProps) {
  const internalProps = mergeDefined(
    {
      p: '1',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'border',
      bg: 'bg.subtle',
      rounded: 'l3',
    },
    props
  )

  return (
    <DetailCardContext value={{collapsible}}>
      {!collapsible && <Box {...internalProps}>{children}</Box>}
      {collapsible && (
        <Accordion.Item value={collapsible} {...internalProps}>
          {children}
        </Accordion.Item>
      )}
    </DetailCardContext>
  )
}

function DetailCardOuter({children, ...props}: BoxProps) {
  const {collapsible} = useDetailCardContext()
  const internalProps = mergeDefined(
    {p: '4', width: '100%', display: 'flex', justifyContent: 'space-between', gapX: '4'},
    props
  )

  return (
    <Box {...internalProps}>
      <Box width="100%">{children}</Box>

      {collapsible && (
        <Accordion.ItemTrigger width="max-content" cursor="pointer">
          <Accordion.ItemIndicator />
        </Accordion.ItemTrigger>
      )}
    </Box>
  )
}

function DetailCardInner({children, ...props}: BoxProps) {
  const {collapsible} = useDetailCardContext()
  const internalProps = mergeDefined(
    {
      p: '4',
      bg: 'bg.panel',
      border: '1px',
      borderStyle: 'solid',
      borderColor: 'border',
      rounded: 'l2',
    },
    props
  )

  if (collapsible)
    return (
      <Accordion.ItemContent css={{overflow: 'hidden', contain: 'layout paint'}}>
        <Box {...internalProps}>{children}</Box>
      </Accordion.ItemContent>
    )

  return <Box {...internalProps}>{children}</Box>
}

interface SectionProps extends Omit<BoxProps, 'title'> {
  title: ReactNode
  description: ReactNode

  icon?: ReactNode
}

// used to display Text as a div if it contains multiple elements
// if displayed as a paragraph, having <div /> as a child is invalid
function pickTextType(children: ReactNode) {
  return typeof children === 'string' ? undefined : 'div'
}

function DetailCardOuterSection({title, description, icon, ...props}: SectionProps) {
  return (
    <Box as="section" wordBreak="break-all" {...props}>
      <Flex gap={3} alignItems="center">
        {icon && (
          <Avatar.Root size="xl" shape="square" rounded="md">
            {icon}
          </Avatar.Root>
        )}
        <Box>
          <Text as={pickTextType(title)} fontSize="md" fontWeight="medium" wordBreak="inherit">
            {title}
          </Text>
          <Text as={pickTextType(description)} fontSize="sm" color="fg.muted" wordBreak="inherit">
            {description}
          </Text>
        </Box>
      </Flex>
    </Box>
  )
}

interface DetailCardInnerSectionProps extends SectionProps {
  variant?: 'muted' | 'fg-muted' | 'fg-fg' | 'muted-fg'
  titleProps?: TextProps
  descriptionProps?: TextProps
}

function DetailCardInnerSection({
  variant,
  title,
  description,
  titleProps,
  descriptionProps,
  ...props
}: DetailCardInnerSectionProps) {
  return (
    <Box as="section" color="fg.muted" fontSize="inherit" {...props}>
      <Text
        as={pickTextType(title)}
        fontWeight="medium"
        color={variant === 'fg-fg' || variant === 'fg-muted' ? 'fg' : undefined}
        {...titleProps}
      >
        {title}
      </Text>
      <Text
        as={pickTextType(description)}
        color={variant === 'fg-fg' || variant === 'muted-fg' ? 'fg' : undefined}
        {...descriptionProps}
      >
        {description}
      </Text>
    </Box>
  )
}

export const DetailCard = {
  Root: DetailCardRoot,
  Outer: DetailCardOuter,
  OuterSection: DetailCardOuterSection,
  Inner: DetailCardInner,
  InnerSection: DetailCardInnerSection,
}
